import { useState } from 'react';
import { Box, useTheme } from '@mui/material';
import { DateField } from '@mui/x-date-pickers/DateField';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { DatePicker } from '@/components/atoms/DatePicker';
import { DateRangePickerProps } from './DateRangePicker.props';
import { isSameDay, isWithinInterval } from 'date-fns';

/**
 * DateRangePicker Component
 *
 * A date range picker with two separate calendar inputs for start and end dates.
 *
 * @example
 * ```tsx
 * const [dateRange, setDateRange] = useState<DateRangeValue>({
 *   startDate: null,
 *   endDate: null,
 * });
 *
 * <DateRangePicker
 *   value={dateRange}
 *   onChange={setDateRange}
 * />
 * ```
 */
export const DateRangePicker = ({
  value,
  onChange,
  size = 's',
  minDate,
  maxDate,
  disablePast = false,
  disableFuture = true,
  dateFormat = 'dd/MM/yyyy',
  allowedDateRange,
  ...props
}: DateRangePickerProps) => {
  const theme = useTheme();
  const [activeInput, setActiveInput] = useState<'start' | 'end'>('start');

  // Calculate effective minDate and maxDate based on allowedDateRange
  const effectiveMinDate = allowedDateRange
    ? allowedDateRange.startDate
    : minDate;
  const effectiveMaxDate = allowedDateRange
    ? allowedDateRange.endDate
    : maxDate;

  const handleStartDateChange = (newDate: Date | null) => {
    onChange({
      ...value,
      startDate: newDate,
    });
    // Auto-switch to end date input after selecting start date from calendar
    if (newDate && activeInput === 'start') {
      setActiveInput('end');
    }
  };

  const handleEndDateChange = (newDate: Date | null) => {
    onChange({
      ...value,
      endDate: newDate,
    });
  };

  const calendarWidth =
    size === 's' ? '290px' : size === 'm' ? '320px' : '350px';

  const currentValue =
    activeInput === 'start' ? value.startDate : value.endDate;

  // Custom day renderer for range visualization
  const renderDay = (
    day: Date,
    _selectedDays: Date[],
    pickersDayProps: PickersDayProps
  ) => {
    const { startDate, endDate } = value;
    const isInRange =
      startDate &&
      endDate &&
      isWithinInterval(day, { start: startDate, end: endDate });

    // Check if start and end dates are the same
    const isSameDate = startDate && endDate && isSameDay(startDate, endDate);

    // Check if this is the opposite date (end when viewing start, start when viewing end)
    // Don't dim if both dates are the same
    const isOppositeDate =
      !isSameDate &&
      ((activeInput === 'start' && endDate && isSameDay(day, endDate)) ||
        (activeInput === 'end' && startDate && isSameDay(day, startDate)));

    return (
      <Box
        sx={{
          position: 'relative',
          ...(isInRange && {
            backgroundColor: theme.palette.M3.sys.light['primary-container'],
            opacity: 1,
          }),
        }}
      >
        <PickersDay
          {...pickersDayProps}
          sx={{
            ...(isOppositeDate && {
              opacity: 0.6,
              border: `1px solid ${theme.palette.M3.sys.light.primary}`,
            }),
          }}
        />
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          borderRadius: theme.unit(2),
          padding: theme.unit(2),
          width: `calc(${calendarWidth} + ${theme.unit(4)})`,
          boxSizing: 'border-box',
        }}
        {...props}
      >
        {/* Date Inputs */}
        <Box
          sx={{
            display: 'flex',
            gap: theme.unit(1),
            marginBottom: theme.unit(2),
            width: '100%',
          }}
        >
          <DateField
            label="Start Date"
            value={value.startDate}
            onChange={handleStartDateChange}
            onClick={() => setActiveInput('start')}
            onFocus={() => setActiveInput('start')}
            format={dateFormat}
            minDate={effectiveMinDate}
            maxDate={effectiveMaxDate}
            slotProps={{
              textField: {
                size: 'small',
                sx: {
                  flex: 1,
                  minWidth: 0,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor:
                      activeInput === 'start'
                        ? theme.palette.M3.sys.light['primary-container']
                        : 'transparent',
                    transition: 'background-color 0.2s, opacity 0.2s',
                    opacity: activeInput === 'start' ? 1 : 0.5,
                    '& fieldset': {
                      borderColor:
                        activeInput === 'start'
                          ? `${theme.palette.M3.sys.light.primary} !important`
                          : theme.palette.M3.sys.light['outline 3'],
                      borderWidth:
                        activeInput === 'start' ? '2px !important' : '1px',
                      transition: 'border-color 0.2s, border-width 0.2s',
                    },
                    '&:hover fieldset': {
                      borderColor:
                        activeInput === 'start'
                          ? `${theme.palette.M3.sys.light.primary} !important`
                          : theme.palette.M3.sys.light.outline,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor:
                        activeInput === 'start'
                          ? `${theme.palette.M3.sys.light.primary} !important`
                          : theme.palette.M3.sys.light.primary,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    ...theme.typography.M3.label.small,
                    color:
                      activeInput === 'start'
                        ? `${theme.palette.M3.sys.light.primary} !important`
                        : undefined,
                    opacity: activeInput === 'start' ? 1 : 0.5,
                    transition: 'opacity 0.2s',
                  },
                  '& .MuiOutlinedInput-input': {
                    /* M3/body/large */
                    ...theme.typography.M3.body.large,
                    textAlign: 'center',
                  },
                },
              },
            }}
          />

          <DateField
            label="End Date"
            value={value.endDate}
            onChange={handleEndDateChange}
            onClick={() => setActiveInput('end')}
            onFocus={() => setActiveInput('end')}
            format={dateFormat}
            minDate={value.startDate || effectiveMinDate}
            maxDate={effectiveMaxDate}
            slotProps={{
              textField: {
                size: 'small',
                sx: {
                  flex: 1,
                  minWidth: 0,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor:
                      activeInput === 'end'
                        ? theme.palette.M3.sys.light['primary-container']
                        : 'transparent',
                    transition: 'background-color 0.2s, opacity 0.2s',
                    opacity: activeInput === 'end' ? 1 : 0.5,
                    '& fieldset': {
                      borderColor:
                        activeInput === 'end'
                          ? `${theme.palette.M3.sys.light.primary} !important`
                          : theme.palette.M3.sys.light['outline 3'],
                      borderWidth:
                        activeInput === 'end' ? '2px !important' : '1px',
                      transition: 'border-color 0.2s, border-width 0.2s',
                    },
                    '&:hover fieldset': {
                      borderColor:
                        activeInput === 'end'
                          ? `${theme.palette.M3.sys.light.primary} !important`
                          : theme.palette.M3.sys.light.outline,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor:
                        activeInput === 'end'
                          ? `${theme.palette.M3.sys.light.primary} !important`
                          : theme.palette.M3.sys.light.primary,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    ...theme.typography.M3.label.small,
                    color:
                      activeInput === 'end'
                        ? `${theme.palette.M3.sys.light.primary} !important`
                        : undefined,
                    opacity: activeInput === 'end' ? 1 : 0.5,
                    transition: 'opacity 0.2s',
                  },
                  '& .MuiOutlinedInput-input': {
                    /* M3/body/large */
                    ...theme.typography.M3.body.large,
                    textAlign: 'center',
                  },
                },
              },
            }}
          />
        </Box>

        {/* Calendar - Reusing DatePicker component */}
        <DatePicker
          value={currentValue}
          onChange={
            activeInput === 'start'
              ? handleStartDateChange
              : handleEndDateChange
          }
          minDate={
            activeInput === 'end'
              ? value.startDate || effectiveMinDate
              : effectiveMinDate
          }
          maxDate={effectiveMaxDate}
          disablePast={disablePast}
          disableFuture={disableFuture}
          size={size}
          slots={{
            day: (props: PickersDayProps) => renderDay(props.day, [], props),
          }}
          sx={{
            padding: 0,
            width: '100%',
            maxWidth: '100%',
          }}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default DateRangePicker;
